import {
  alpha,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import { Link as RouterLink } from 'react-router';
import type { AdapterServiceDto } from '../../domain/adapterService';

type Props = {
  service: AdapterServiceDto;
  onDelete: (service: AdapterServiceDto) => void;
};

function isPublished(service: AdapterServiceDto): boolean {
  return service.status === 'published';
}

function formatServiceTimestamp(value: string): string {
  return new Date(value).toLocaleString();
}

function footerText(service: AdapterServiceDto): string {
  if (isPublished(service)) {
    const when = service.lastPublishedAt ?? service.updatedAt;
    return `Published ${formatServiceTimestamp(when)}`;
  }
  const draftSuffix = service.capabilityCount === 0 ? ' · no capabilities yet' : '';
  return `Draft · last edited ${formatServiceTimestamp(service.updatedAt)}${draftSuffix}`;
}

export function ServiceOnboardingCard({ service, onDelete }: Props) {
  const theme = useTheme();
  const published = isPublished(service);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        position: 'relative',
        borderLeftWidth: 4,
        borderLeftStyle: 'solid',
        borderLeftColor: published ? 'success.main' : 'warning.main',
        bgcolor: published
          ? 'background.paper'
          : alpha(theme.palette.warning.main, 0.04),
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 2,
        }}
      >
        <IconButton
          size="small"
          aria-label={`Delete ${service.displayName}`}
          sx={{
            bgcolor: alpha(theme.palette.background.paper, 0.92),
            boxShadow: theme.shadows[1],
            color: 'text.secondary',
            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.12), color: 'error.main' },
          }}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onDelete(service);
          }}
        >
          <DeleteOutlineRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
      <CardActionArea component={RouterLink} to={`/service-onboarding/${service.id}`}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, pr: 4 }}>
            <HubRoundedIcon color={published ? 'success' : 'disabled'} fontSize="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
              {service.displayName}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block">
            Service key: <code>{service.adapterServiceKey}</code> · label{' '}
            <code>{service.configLabel ?? 'default'}</code>
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={published ? 'Published' : 'Draft'}
              color={published ? 'success' : 'warning'}
              variant="filled"
            />
            <Chip
              size="small"
              label={service.flowType === 'multi_step' ? 'Multi-step' : 'Single'}
              variant="outlined"
            />
            <Chip size="small" label={`${service.capabilityCount} capabilities`} variant="outlined" />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {footerText(service)}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
